// -----------------------------------------------------------------------------
// BulkAssignModal  (Task #52)
//
// Modal for assigning team members to ALL active (non-done) checklist tasks
// at once. Opens from the "Assign all" button in the checklist stats strip.
//
// Logic:
//   • Pre-selects the INTERSECTION of assignees already set on all active tasks
//     (only users assigned to EVERY task are pre-selected, so the user can see
//     a sensible default without accidentally overwriting partial assignments)
//   • On Save: N parallel PATCHes via Promise.allSettled, one per active taskId
//     PATCH /api/v1/practices/{practiceId}/tasks/{taskId}/
//     body: { assignees: [{ practice_user_id: N }] }
//   • Reports the number of successfully updated tasks
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, CircularProgress, Box,
} from '@mui/material';
import AssignedToLead from 'src/components/AssignedToLead';
import usePracticeStaff from 'src/hooks/usePracticeStaff';
import { callApi } from 'src/utils/apiCall';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns the PracticeUser objects (from assignableUsers) that appear in
 * EVERY task's raw assignee list.
 *
 * @param {Array} tasks       - flat items with rawTask.assignees [{id: user_id, name}]
 * @param {Array} assignableUsers - PracticeUser[] from usePracticeStaff
 */
function computeIntersection(tasks, assignableUsers) {
    if (!tasks.length || !assignableUsers.length) return [];

    // Build a Set<user_id> for each task
    const taskUserIdSets = tasks.map((t) => new Set((t.rawTask?.assignees || []).map((a) => a.id)));

    // A user_id is in the intersection if it appears in ALL tasks
    const firstSet = taskUserIdSets[0];
    const intersectedUserIds = new Set(
        [...firstSet].filter((uid) => taskUserIdSets.every((s) => s.has(uid)))
    );

    return assignableUsers.filter((pu) => intersectedUserIds.has(pu.user?.id));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BulkAssignModal({ open, onClose, practiceId, activeTasks, onSaved }) {
    const { assignableUsers, isLoaded } = usePracticeStaff();
    const [saving, setSaving] = useState(false);

    // Pre-select intersection of common assignees
    const intersection = useMemo(
        () => (isLoaded ? computeIntersection(activeTasks, assignableUsers) : []),
        [isLoaded, activeTasks, assignableUsers]
    );

    const [selected, setSelected] = useState(null); // null = lazy init from intersection
    const effectiveSelected = selected !== null ? selected : intersection;

    // Reset selection when modal opens
    const handleEnter = () => setSelected(null);

    const handleSave = async () => {
        if (!practiceId) return;
        const taskIds = activeTasks.map((t) => t.taskId).filter(Boolean);
        if (!taskIds.length) {
            onClose();
            return;
        }
        setSaving(true);
        try {
            const payload = { assignees: effectiveSelected.map((pu) => ({ practice_user_id: pu.id })) };
            const results = await Promise.allSettled(
                taskIds.map((taskId) =>
                    callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'patch', payload)
                )
            );

            const succeeded = results.filter((r) => r.status === 'fulfilled' && r.value?.ok).length;
            const failed = results.length - succeeded;

            if (failed > 0) {
                dispatch(updateError({
                    success: false,
                    message: `Assigned ${succeeded}/${taskIds.length} tasks — ${failed} failed`,
                }));
            }

            onClose();
            if (onSaved) onSaved(effectiveSelected, succeeded);
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Something went wrong' }));
        } finally {
            setSaving(false);
        }
    };

    const taskCount = activeTasks.length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            TransitionProps={{ onEnter: handleEnter }}
            data-testid="bulk-assign-modal"
        >
            <DialogTitle sx={{ fontSize: '16px', fontWeight: 700, pb: 0.5 }}>
                Assign team members
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                <Typography sx={{ fontSize: '13px', color: '#6e6e73', mb: 2 }}>
                    {taskCount === 1
                        ? 'This will be applied to 1 active task.'
                        : `This will be applied to all ${taskCount} active tasks.`}
                    {intersection.length > 0 && (
                        <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '12px' }}>
                            Pre-selected: users already assigned to every task.
                        </Box>
                    )}
                </Typography>

                {isLoaded ? (
                    <AssignedToLead
                        assignToState={effectiveSelected}
                        setAssignTo={setSelected}
                        staffRole="assignee"
                    />
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button
                    onClick={onClose}
                    sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '14px' }}
                    data-testid="bulk-assign-cancel"
                >
                    Cancel
                </Button>
                <Button
                    data-testid="bulk-assign-save"
                    variant="contained"
                    disableElevation
                    disabled={saving || !isLoaded}
                    onClick={handleSave}
                    sx={{ textTransform: 'none', fontSize: '14px', fontWeight: 600 }}
                >
                    {saving
                        ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                        : `Apply to ${taskCount} task${taskCount !== 1 ? 's' : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

BulkAssignModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /** Practice ID for PATCH calls. */
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    /** Active (non-done) flat checklist items — each has taskId + rawTask.assignees. */
    activeTasks: PropTypes.arrayOf(PropTypes.shape({
        taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        rawTask: PropTypes.object,
    })),
    /** Called after successful bulk save with (selectedUsers, successCount). */
    onSaved: PropTypes.func,
};

BulkAssignModal.defaultProps = {
    activeTasks: [],
    onSaved: null,
};
